import { useEffect, useState, useRef, RefObject, useCallback } from 'react'
import { BreakpointKey } from 'types'
import { breakpoints } from 'utils/styles/breakpoints'

/**
 * Check's to see if dom element (ref) is viewable within viewport.
 *
 * @param {RefObject<HTMLElement>} ref
 * @param {boolean} triggerOnce - optional parameter to trigger css only once
 * @returns {boolean} true means the dom element is within viewport, false means it is not
 */

function useOnScreen(ref: RefObject<HTMLElement>, triggerOnce?: boolean, rootMargin?: string) {
  const observerRef = useRef<IntersectionObserver | null>(null)
  const [isOnScreen, setIsOnScreen] = useState(false)

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio > 0 && triggerOnce) {
          setIsOnScreen(entry.isIntersecting)
        } else if (entry.intersectionRatio > 0) {
          setIsOnScreen(entry.isIntersecting)
        } else if (entry.intersectionRatio === 0) {
          setIsOnScreen(false)
        }
      },
      { rootMargin: rootMargin || '-30px' }
    )
    ref.current && observerRef.current?.observe(ref.current)
    return () => {
      observerRef.current?.disconnect()
    }
  }, [])
  return isOnScreen
}

/**
 * Executes callback if clicked outside referenced dom element
 * @param {RefObject<HTMLElement>} ref
 * @param {*} callback - function used to change state
 */

function useOutsideClick(ref: RefObject<HTMLElement>, callback: () => void): void {
  const handleClick = (e: { target: EventTarget | null }) => {
    if (ref.current && !ref.current.contains(e.target as Node)) {
      callback()
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleClick)

    return () => document.removeEventListener('click', handleClick)
  })
}

/**
 * Returns window size and whether size is above or below a specified breakpoint
 * if function is done calculating user's screen size on every window resize event
 * @param {string} breakpoint
 * @param {string} operator
 * @param {callback} callback - optional parameter that runs a function if needed
 * @returns {{number, boolean, boolean}} - object that returns window size, whether or not it's on or below breakpoint,
 * and if function is done calculating user's screen size
 */
const operators = {
  '<': (a: number, b: number) => a < b,
  '<=': (a: number, b: number) => a <= b,
  '>': (a: number, b: number) => a > b,
  '>=': (a: number, b: number) => a >= b
} as const

type OperatorKey = keyof typeof operators

function useWindowSize(
  breakpoint: BreakpointKey,
  operator: OperatorKey,
  callback?: () => void
): { windowSize: number; isBreakpoint: boolean | null; windowSizeCalculation: boolean } {
  const [windowSize, setWindowSize] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 0)
  const [isBreakpoint, setIsBreakpoint] = useState<boolean | null>(null)
  const [windowSizeCalculation, setWindowSizeCalculation] = useState<boolean>(false)

  const breakpointMapped = breakpoints[breakpoint]
  const operatorFn = operators[operator]

  const memoizedCallback = useCallback(() => {
    if (callback) callback()
  }, [callback])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleResize = () => {
      const newWindowSize = window.innerWidth
      setWindowSize(newWindowSize)

      if (operatorFn && typeof breakpointMapped === 'number') {
        const breakpointBoolean = operatorFn(newWindowSize, breakpointMapped)
        setIsBreakpoint(breakpointBoolean)

        if (!breakpointBoolean && memoizedCallback) {
          memoizedCallback()
        }
      }
    }

    handleResize()
    setWindowSizeCalculation(true)

    let timeoutId: NodeJS.Timeout
    const debouncedResize = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(handleResize, 100)
    }

    window.addEventListener('resize', debouncedResize)

    return () => {
      window.removeEventListener('resize', debouncedResize)
      clearTimeout(timeoutId)
    }
  }, [breakpointMapped, operatorFn, memoizedCallback])

  return { windowSize, isBreakpoint: isBreakpoint ?? false, windowSizeCalculation }
}

function useIsSmartphoneLandscape() {
  const [isSmartphoneLandscape, setIsSmartphoneLandscape] = useState(
    typeof window !== 'undefined' ? window.innerWidth > window.innerHeight * 2 && window.innerHeight <= 430 : false
  )

  useEffect(() => {
    const handleResize = () => {
      setIsSmartphoneLandscape(window.innerWidth > window.innerHeight * 1.6 && window.innerHeight <= 430)
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return isSmartphoneLandscape
}

export { useOnScreen, useOutsideClick, useWindowSize, useIsSmartphoneLandscape }
